import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { useMarkPageVisited } from '../hooks/usePageVisit';
import TrialExpiredOverlay from '../components/TrialExpiredOverlay';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Truck, 
  Plus, 
  Pencil, 
  Trash2, 
  Search,
  Loader2,
  Calendar,
  Snowflake,
  Apple,
  Sparkles,
  Package,
  Wine,
  ShoppingBasket,
  Wrench,
  GlassWater,
  Fish,
  X,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';

const SUPPLIER_TYPES = [
  { value: 'froid', label: 'Froid', icon: Truck, color: 'bg-blue-100 text-blue-700' },
  { value: 'surgele', label: 'Surgelé', icon: Snowflake, color: 'bg-cyan-100 text-cyan-700' },
  { value: 'poisson_frais', label: 'Poisson Frais', icon: Fish, color: 'bg-sky-100 text-sky-700' },
  { value: 'boisson', label: 'Boisson', icon: GlassWater, color: 'bg-amber-100 text-amber-700' },
  { value: 'fruits_legumes', label: 'Fruit et légume', icon: Apple, color: 'bg-green-100 text-green-700' },
  { value: 'alcool', label: 'Alcool', icon: Wine, color: 'bg-purple-100 text-purple-700' },
  { value: 'hygiene', label: 'Hygiène', icon: Sparkles, color: 'bg-pink-100 text-pink-700' },
  { value: 'epicerie', label: 'Épicerie', icon: ShoppingBasket, color: 'bg-orange-100 text-orange-700' },
  { value: 'non_alimentaire', label: 'Non-alimentaire', icon: Package, color: 'bg-gray-100 text-gray-700' },
  { value: 'equipement', label: 'Équipement', icon: Wrench, color: 'bg-slate-100 text-slate-700' }
];

const DAYS = [
  { value: 0, label: 'Lundi' },
  { value: 1, label: 'Mardi' },
  { value: 2, label: 'Mercredi' },
  { value: 3, label: 'Jeudi' },
  { value: 4, label: 'Vendredi' },
  { value: 5, label: 'Samedi' },
  { value: 6, label: 'Dimanche' }
];

const AdminSuppliersPage = () => {
  const { api } = useAuth();
  const { isTrialExpired } = useTrialStatus();
  
  // Mark this page as visited
  useMarkPageVisited('/admin/suppliers');
  
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    types: [],
    email: '',
    secondary_emails: [],
    address: '',
    delivery_days: [],
    lead_time_days: 1,
    cutoff_hour: 12
  });

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/suppliers/all');
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      toast.error('Erreur lors du chargement des fournisseurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name.toLowerCase().includes(search.toLowerCase()) ||
    supplier.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenDialog = (supplier = null) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setFormData({
        name: supplier.name,
        types: supplier.types || [],
        email: supplier.email,
        secondary_emails: supplier.secondary_emails || [],
        address: supplier.address || '',
        delivery_days: supplier.delivery_days || [],
        lead_time_days: supplier.lead_time_days || 1,
        cutoff_hour: supplier.cutoff_hour || 12
      });
    } else {
      setEditingSupplier(null);
      setFormData({
        name: '',
        types: [],
        email: '',
        secondary_emails: [],
        address: '',
        delivery_days: [],
        lead_time_days: 1,
        cutoff_hour: 12
      });
    }
    setDialogOpen(true);
  };

  const addSecondaryEmail = () => {
    if (formData.secondary_emails.length >= 4) {
      toast.error('Maximum 4 emails secondaires autorisés');
      return;
    }
    setFormData({
      ...formData,
      secondary_emails: [...formData.secondary_emails, '']
    });
  };

  const updateSecondaryEmail = (index, value) => {
    const newEmails = [...formData.secondary_emails];
    newEmails[index] = value;
    setFormData({
      ...formData,
      secondary_emails: newEmails
    });
  };

  const removeSecondaryEmail = (index) => {
    setFormData({
      ...formData,
      secondary_emails: formData.secondary_emails.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || formData.delivery_days.length === 0) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (formData.types.length === 0) {
      toast.error('Veuillez sélectionner au moins un type');
      return;
    }

    // Validate secondary emails
    const validSecondaryEmails = formData.secondary_emails.filter(email => email.trim() !== '');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const email of validSecondaryEmails) {
      if (!emailRegex.test(email)) {
        toast.error(`Email secondaire invalide: ${email}`);
        return;
      }
    }

    setFormLoading(true);
    try {
      const dataToSend = {
        ...formData,
        secondary_emails: validSecondaryEmails
      };
      
      if (editingSupplier) {
        await api.put(`/suppliers/${editingSupplier.id}`, dataToSend);
        toast.success('Fournisseur modifié');
      } else {
        await api.post('/suppliers', dataToSend);
        toast.success('Fournisseur créé');
      }
      
      setDialogOpen(false);
      fetchSuppliers();
    } catch (error) {
      // Gérer les erreurs de validation Pydantic qui peuvent être un tableau d'objets
      let errorMessage = 'Erreur lors de l\'opération';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Si c'est un tableau d'erreurs de validation
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } 
        // Si c'est une chaîne
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Si c'est un objet (ex: limite de plan atteinte)
        else if (typeof detail === 'object') {
          errorMessage = detail.message || detail.msg || JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce fournisseur ?')) return;
    
    try {
      await api.delete(`/suppliers/${supplierId}`);
      toast.success('Fournisseur supprimé');
      fetchSuppliers();
    } catch (error) {
      // Gérer les erreurs proprement
      let errorMessage = 'Erreur lors de la suppression';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = detail.message || detail.msg || JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const toggleDeliveryDay = (day) => {
    setFormData(prev => ({
      ...prev,
      delivery_days: prev.delivery_days.includes(day)
        ? prev.delivery_days.filter(d => d !== day)
        : [...prev.delivery_days, day].sort((a, b) => a - b)
    }));
  };

  const toggleType = (typeValue) => {
    setFormData(prev => ({
      ...prev,
      types: prev.types.includes(typeValue)
        ? prev.types.filter(t => t !== typeValue)
        : [...prev.types, typeValue]
    }));
  };

  const getTypeInfo = (typeValue) => {
    return SUPPLIER_TYPES.find(t => t.value === typeValue);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4332]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in relative" data-testid="admin-suppliers-page">
      {isTrialExpired && <TrialExpiredOverlay />}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Fournisseurs</h1>
          <p className="text-gray-500 mt-1">Gestion des fournisseurs</p>
          <p className="text-sm text-gray-600 mt-2 max-w-2xl">
            Enregistrez vos fournisseurs, configurez leurs jours de livraison, délais de commande et heures limites d'envoi.
          </p>
        </div>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
          data-testid="add-supplier-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un fournisseur
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          placeholder="Rechercher un fournisseur..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="suppliers-search-input"
        />
      </div>

      <div className="grid gap-4">
        {filteredSuppliers.map((supplier) => (
          <Card key={supplier.id} className="border border-gray-200" data-testid={`supplier-card-${supplier.name}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#D8F3DC] flex items-center justify-center">
                    <Truck className="w-6 h-6 text-[#1B4332]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{supplier.name}</h3>
                    <div className="flex flex-wrap gap-1 mb-1">
                      {(supplier.types || []).map((typeValue) => {
                        const typeInfo = getTypeInfo(typeValue);
                        return typeInfo ? (
                          <Badge key={typeValue} variant="outline" className={`text-xs ${typeInfo.color}`}>
                            {typeInfo.label}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                    <p className="text-sm text-gray-500">{supplier.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <div className="flex gap-1">
                        {supplier.delivery_days.map((day) => (
                          <span 
                            key={day} 
                            className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                          >
                            {DAYS.find(d => d.value === day)?.label.slice(0, 3)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    size="icon" 
                    variant="outline"
                    onClick={() => handleOpenDialog(supplier)}
                    data-testid={`edit-supplier-${supplier.name}-btn`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="outline"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(supplier.id)}
                    data-testid={`delete-supplier-${supplier.name}-btn`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSupplier ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
            </DialogTitle>
            <DialogDescription>
              {editingSupplier ? 'Modifiez les informations du fournisseur' : 'Ajoutez un nouveau fournisseur'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="supplier-name-input"
              />
            </div>

            <div className="space-y-3">
              <Label>Types * <span className="text-gray-400 text-sm font-normal">(plusieurs choix possibles)</span></Label>
              <div className="grid grid-cols-2 gap-2">
                {SUPPLIER_TYPES.map((type) => {
                  const Icon = type.icon;
                  const isSelected = formData.types.includes(type.value);
                  return (
                    <div
                      key={type.value}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-[#1B4332] bg-[#D8F3DC]/30' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => toggleType(type.value)}
                      data-testid={`supplier-type-${type.value}-checkbox`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleType(type.value)}
                        className="pointer-events-none"
                      />
                      <Icon className={`w-4 h-4 ${isSelected ? 'text-[#1B4332]' : 'text-gray-400'}`} />
                      <span className={`text-sm ${isSelected ? 'font-medium text-[#1B4332]' : 'text-gray-600'}`}>
                        {type.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="supplier-email-input"
              />
            </div>

            {/* Secondary Emails Section */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Emails secondaires (optionnel)</Label>
                  <p className="text-xs text-gray-500 mt-1">
                    Ajoutez jusqu'à 4 emails supplémentaires pour recevoir les commandes
                  </p>
                </div>
                {formData.secondary_emails.length < 4 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addSecondaryEmail}
                    className="text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Ajouter
                  </Button>
                )}
              </div>

              {formData.secondary_emails.length > 0 && (
                <div className="space-y-2">
                  {formData.secondary_emails.map((email, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => updateSecondaryEmail(index, e.target.value)}
                        placeholder={`Email secondaire ${index + 1}`}
                        className="flex-1"
                        data-testid={`secondary-email-${index}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSecondaryEmail(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500">
                    {formData.secondary_emails.length}/4 emails secondaires
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Adresse</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                data-testid="supplier-address-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Jours de livraison *</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((day) => (
                  <Button
                    key={day.value}
                    type="button"
                    size="sm"
                    variant={formData.delivery_days.includes(day.value) ? 'default' : 'outline'}
                    className={formData.delivery_days.includes(day.value) ? 'bg-[#1B4332] hover:bg-[#0D2E1F]' : ''}
                    onClick={() => toggleDeliveryDay(day.value)}
                    data-testid={`supplier-day-${day.label}-btn`}
                  >
                    {day.label.slice(0, 3)}
                  </Button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="lead_time_days">Délai commande (jours) *</Label>
                <Input
                  id="lead_time_days"
                  type="number"
                  min="0"
                  max="7"
                  value={formData.lead_time_days}
                  onChange={(e) => setFormData({ ...formData, lead_time_days: parseInt(e.target.value) || 1 })}
                  data-testid="supplier-lead-time-input"
                />
                <p className="text-xs text-gray-500">Nb de jours avant la livraison</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cutoff_hour">Heure limite *</Label>
                <Input
                  id="cutoff_hour"
                  type="number"
                  min="0"
                  max="23"
                  value={formData.cutoff_hour}
                  onChange={(e) => setFormData({ ...formData, cutoff_hour: parseInt(e.target.value) || 12 })}
                  data-testid="supplier-cutoff-hour-input"
                />
                <p className="text-xs text-gray-500">Ex: 12 = 12h00</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={formLoading}
              className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
              data-testid="save-supplier-btn"
            >
              {formLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSuppliersPage;
